export const CART_OPERATION_KEY = 'lups-selector-cart-operation:v1';

const LABELS = Object.freeze({
  add: 'Añadir al carrito',
  loading: 'Añadiendo tus fundas…',
  disabled: 'La compra todavía no está disponible en esta vista de prueba.',
  error: 'No se han podido añadir tus fundas. Revisa la selección e inténtalo de nuevo.',
  partial: 'No están disponibles todas las unidades elegidas. Revisa las fundas añadidas en tu carrito.',
  uncertain: 'Revisa tu carrito antes de volver a añadir estas fundas.',
  priceChanged: 'El importe actual de tu selección es {total}. Revísalo en el carrito antes de continuar.',
  review: 'Ver mi carrito',
  added: 'Tus fundas están en el carrito.',
  currencyChanged: 'Los precios han cambiado al seleccionar otro destino. Recarga la página y revisa tu selección.'
});

const amount = (value) => Number.isSafeInteger(value) && value >= 0;
const currencyCode = (value) => typeof value === 'string' && /^[A-Z]{3}$/.test(value);
const variantId = (value) => typeof value === 'string' && /^[1-9][0-9]*$/.test(value);
const operationToken = (value) => typeof value === 'string' && /^[a-zA-Z0-9-]{8,80}$/.test(value);
const propertyToken = (line) => line.properties?._lups_selection;

function fault(code) {
  return Object.assign(new Error(code), { code });
}

function checkedCart(cart) {
  if (!cart || !Array.isArray(cart.items) || !currencyCode(cart.currency)) throw fault('invalid_cart');
  for (const line of cart.items) {
    if (!variantId(String(line.variant_id ?? line.id)) || !Number.isSafeInteger(line.quantity) || line.quantity <= 0) throw fault('invalid_cart');
  }
  return cart;
}

function sumSafe(values) {
  let total = 0n;
  for (const value of values) {
    if (!amount(value)) return null;
    total += BigInt(value);
  }
  return total <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(total) : null;
}

function quantitiesByVariant(lines) {
  const result = new Map();
  for (const line of lines) {
    const id = String(line.variant_id ?? line.id);
    const next = sumSafe([result.get(id) || 0, line.quantity]);
    if (next === null) throw fault('invalid_cart');
    result.set(id, next);
  }
  return result;
}

function lineIdentity(line) {
  const properties = Object.entries(line.properties || {}).sort(([a], [b]) => a.localeCompare(b));
  const sellingPlan = line.selling_plan_allocation?.selling_plan?.id ?? null;
  return JSON.stringify([String(line.variant_id ?? line.id), properties, sellingPlan]);
}

function quantitiesByIdentity(lines) {
  const result = new Map();
  for (const line of lines) {
    const key = lineIdentity(line);
    const next = sumSafe([result.get(key) || 0, line.quantity]);
    if (next === null) throw fault('invalid_cart');
    result.set(key, next);
  }
  return result;
}

export function buildSelectionItems(quantities, catalog, token) {
  if (!operationToken(token) || !quantities || typeof quantities !== 'object' || Array.isArray(quantities) || !Array.isArray(catalog)) throw fault('invalid_selection');
  const items = [];
  for (const [id, quantity] of Object.entries(quantities)) {
    const color = catalog.find((item) => String(item.id) === id);
    if (!variantId(id) || !color || !Number.isSafeInteger(quantity) || quantity <= 0) throw fault('invalid_selection');
    if (color.available === false) throw fault('stock');
    if (color.stock !== null && color.stock !== undefined && color.stock !== '') {
      if (!['number', 'string'].includes(typeof color.stock)) throw fault('stock');
      const stock = Number(color.stock);
      if (!Number.isFinite(stock) || quantity > Math.max(0, Math.floor(stock))) throw fault('stock');
    }
    items.push({ id, quantity, properties: { _lups_selection: token } });
  }
  if (!items.length || sumSafe(items.map((item) => item.quantity)) === null) throw fault('invalid_selection');
  return items;
}

export function createCartApi({ root, cartUrl, origin, fetchImpl }) {
  if (typeof fetchImpl !== 'function' || typeof root !== 'string' || !root.startsWith('/') || root.startsWith('//') || !root.endsWith('/')) throw fault('invalid_routes');
  const base = new URL(root, origin);
  const destination = new URL(cartUrl || `${root}cart`, origin);
  if (base.origin !== origin || base.search || base.hash || destination.origin !== origin || !destination.pathname.endsWith('/cart') || destination.search || destination.hash) throw fault('invalid_routes');
  const request = async (suffix, payload) => {
    const response = await fetchImpl(new URL(suffix, base).href, {
      method: payload ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
      ...(payload ? { body: JSON.stringify(payload) } : {})
    });
    let result;
    try { result = await response.json(); } catch { throw Object.assign(fault('invalid_response'), { httpStatus: response.status }); }
    if (!response.ok || Number(result?.status) >= 400) throw Object.assign(fault('request'), { httpStatus: response.status, response: result });
    return result;
  };
  return {
    read: async () => checkedCart(await request('cart.js')),
    add: (payload) => request('cart/add.js', payload),
    cartUrl: destination.href
  };
}

export function verifyCartOperation(before, after, operation, catalog, { resumed = false } = {}) {
  checkedCart(after);
  if (!operationToken(operation?.token) || !Array.isArray(operation.items) || !operation.items.length) throw fault('invalid_operation');
  const owned = after.items.filter((line) => propertyToken(line) === operation.token);
  const actual = quantitiesByVariant(owned);
  const expected = quantitiesByVariant(operation.items);
  const unexpected = [...actual].some(([id, quantity]) => !expected.has(id) || quantity > expected.get(id));
  const complete = !unexpected && expected.size === actual.size && [...expected].every(([id, quantity]) => actual.get(id) === quantity);
  const beforeCart = before ? checkedCart(before) : null;
  const outsideBefore = beforeCart ? quantitiesByIdentity(beforeCart.items.filter((line) => propertyToken(line) !== operation.token)) : new Map();
  const outsideAfter = quantitiesByIdentity(after.items.filter((line) => propertyToken(line) !== operation.token));
  const outsidePreserved = beforeCart ? [...outsideBefore].every(([id, quantity]) => (outsideAfter.get(id) || 0) >= quantity) : null;
  const eligible = new Set((catalog || []).map((item) => String(item.id)));
  const otherEligibleQuantity = beforeCart ? sumSafe(beforeCart.items.filter((line) => eligible.has(String(line.variant_id ?? line.id))).map((line) => line.quantity)) : null;
  const otherEligibleQuantityAfter = sumSafe(after.items.filter((line) => propertyToken(line) !== operation.token && eligible.has(String(line.variant_id ?? line.id))).map((line) => line.quantity));
  const ownedSubtotal = sumSafe(owned.map((line) => line.final_line_price));
  const onlyOwned = owned.length === after.items.length;
  const actualSelectionTotal = onlyOwned && amount(after.total_price) ? after.total_price : ownedSubtotal;
  const noCartDiscounts = Array.isArray(after.cart_level_discount_applications) && after.cart_level_discount_applications.length === 0;
  const pricingKnown = onlyOwned ? amount(after.total_price) : amount(ownedSubtotal) && noCartDiscounts;
  const comparable = !resumed && complete && outsidePreserved === true && otherEligibleQuantity === 0 && otherEligibleQuantityAfter === 0 && after.currency === operation.currency && pricingKnown && amount(actualSelectionTotal) && amount(operation.expectedTotal);
  const priceCheck = comparable ? actualSelectionTotal === operation.expectedTotal ? 'matched' : 'different' : 'not_comparable';
  const status = unexpected || outsidePreserved === false ? 'uncertain' : complete ? priceCheck === 'different' ? 'price_changed' : 'complete' : owned.length ? 'partial' : 'absent';
  return { status, priceCheck, comparable, actualSelectionTotal, cartTotal: amount(after.total_price) ? after.total_price : null, currency: after.currency, otherEligibleQuantity, otherEligibleQuantityAfter, outsidePreserved, cart: after, owned };
}

export async function commitSelection(api, options) {
  const { enabled = false, backendVerified = false, quantities, catalog, token, currency, expectedTotal, onPrepared } = options;
  if (enabled !== true || backendVerified !== true) return { status: 'disabled', mutationAttempted: false };
  if (!currencyCode(currency) || !amount(expectedTotal)) return { status: 'invalid_selection', mutationAttempted: false };
  let items;
  let before;
  try {
    items = buildSelectionItems(quantities, catalog, token);
    before = checkedCart(await api.read());
  } catch (error) { return { status: error.code || 'read_failed', mutationAttempted: false }; }
  if (before.currency !== currency) return { status: 'currency_changed', mutationAttempted: false, cart: before };
  if (before.items.some((line) => propertyToken(line) === token)) {
    return { ...verifyCartOperation(null, before, { token, items, currency, expectedTotal }, catalog, { resumed: true }), mutationAttempted: false, resumed: true };
  }
  const operation = { version: 1, token, items, currency, expectedTotal };
  if (typeof onPrepared === 'function') {
    try {
      if (onPrepared(operation) === false) return { status: 'storage_unavailable', mutationAttempted: false };
    } catch { return { status: 'storage_unavailable', mutationAttempted: false }; }
  }
  let addError;
  let added;
  try { added = await api.add({ items }); } catch (error) { addError = error; }
  let after;
  try { after = checkedCart(await api.read()); } catch { return { status: 'uncertain', mutationAttempted: true, operation, addResponse: added }; }
  const verification = verifyCartOperation(before, after, operation, catalog);
  const status = verification.status === 'absent' ? addError?.httpStatus ? 'not_added' : 'uncertain' : verification.status;
  return { ...verification, status, mutationAttempted: true, operation, addResponse: added, httpStatus: addError?.httpStatus ?? null };
}

export function saveCartOperation(storage, operation) {
  try {
    if (!storage) return false;
    storage.setItem(CART_OPERATION_KEY, JSON.stringify(operation));
    return true;
  } catch { return false; }
}

export function readCartOperation(storage) {
  try {
    const value = JSON.parse(storage?.getItem(CART_OPERATION_KEY) || 'null');
    if (value?.version !== 1 || !operationToken(value.token) || !currencyCode(value.currency) || !amount(value.expectedTotal) || !Array.isArray(value.items) || !value.items.length) return null;
    const total = sumSafe(value.items.map((item) => Number.isSafeInteger(item.quantity) && item.quantity > 0 && variantId(String(item.id)) && item.properties?._lups_selection === value.token ? item.quantity : NaN));
    return total !== null && total > 0 ? value : null;
  } catch { return null; }
}

function removeCartOperation(storage) {
  try { storage?.removeItem(CART_OPERATION_KEY); } catch {}
}

const BaseElement = globalThis.HTMLElement || class {};
let activeCommit = false;

export class LupsSelectorCart extends BaseElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    let config;
    try { config = JSON.parse(this.querySelector('[data-selector-cart-config]')?.textContent || '{}'); } catch { config = {}; }
    this.config = config;
    this.labels = { ...LABELS, ...(config.labels || {}) };
    this.selector = this.closest('lups-color-selector');
    this.button = this.querySelector('[data-live-cart-add]');
    this.status = this.querySelector('[data-live-cart-status]');
    this.review = this.querySelector('[data-live-cart-review]');
    this.enabled = config.enabled === true && config.backendVerified === true && currencyCode(config.currency);
    this.hidden = !this.enabled;
    if (!this.enabled || !this.selector || !this.button || !this.status || !this.review) return;
    this.status.textContent = '';
    this.review.hidden = true;
    this.setAttribute('aria-busy', 'false');
    this.review.textContent = this.labels.review;
    try {
      this.storage = globalThis.sessionStorage;
    } catch { this.storage = null; }
    try {
      this.api = createCartApi({ root: globalThis.Shopify?.routes?.root, cartUrl: config.cartUrl, origin: globalThis.location.origin, fetchImpl: globalThis.fetch.bind(globalThis) });
    } catch {
      this.button.disabled = true;
      this.status.textContent = this.labels.error;
      return;
    }
    this.review.href = this.api.cartUrl;
    this.selector.addEventListener('lups:selection-change', (event) => {
      if (event.target !== this.selector) return;
      this.quote = event.detail;
      this.updateButton();
    });
    this.button.addEventListener('click', () => this.submit());
    this.button.textContent = this.labels.add;
    this.updateButton();
    queueMicrotask(() => {
      if (typeof this.selector.renderSummary === 'function' && this.selector.catalog && this.selector.market) this.selector.renderSummary();
      this.resume();
    });
  }

  updateButton() {
    if (this.button) this.button.disabled = !this.enabled || this.busy || this.reviewRequired || !this.quote?.count || !amount(this.quote.total) || this.selector?.requireServerQuote === true && this.quote.quoteReady !== true;
  }

  setBusy(busy) {
    this.busy = busy;
    this.setAttribute('aria-busy', String(busy));
    if (busy) {
      this.blockedControls = Array.from(this.selector.querySelectorAll('[data-color-toggle], [data-color-decrease], [data-color-increase], [data-clear-selection], [data-market-country], [data-country-toggle], [data-country-option], [data-preview-checkout], [data-quote-retry]')).map((control) => ({ control, disabled: control.disabled }));
      this.blockedControls.forEach(({ control }) => { control.disabled = true; });
    } else {
      this.blockedControls?.forEach(({ control, disabled }) => { control.disabled = disabled; });
      this.blockedControls = [];
      this.selector.renderSummary?.();
    }
    this.button.textContent = busy ? this.labels.loading : this.labels.add;
    this.updateButton();
  }

  async resume() {
    const operation = readCartOperation(this.storage);
    if (!operation || this.busy || activeCommit) return;
    activeCommit = true;
    this.setBusy(true);
    try {
      const cart = await this.api.read();
      const result = verifyCartOperation(null, cart, operation, this.selector.catalog, { resumed: true });
      this.showResult({ ...result, operation, resumed: true });
    } catch {
      this.showResult({ status: 'uncertain' });
    } finally {
      activeCommit = false;
      this.setBusy(false);
    }
  }

  async submit() {
    if (!this.enabled || this.busy || this.reviewRequired || activeCommit) return;
    if (typeof this.selector.renderSummary === 'function') this.selector.renderSummary();
    if (!this.quote?.count || !amount(this.quote.total) || this.selector.requireServerQuote === true && this.quote.quoteReady !== true) return;
    const token = globalThis.crypto?.randomUUID?.();
    if (!operationToken(token)) {
      this.status.textContent = this.labels.error;
      return;
    }
    activeCommit = true;
    this.setBusy(true);
    this.status.textContent = this.labels.loading;
    this.review.hidden = true;
    try {
      const result = await commitSelection(this.api, {
        enabled: this.config.enabled, backendVerified: this.config.backendVerified,
        quantities: { ...this.selector.quantities }, catalog: this.selector.catalog,
        token, currency: this.selector.market.currency, expectedTotal: this.quote.total,
        onPrepared: (operation) => saveCartOperation(this.storage, operation)
      });
      this.showResult(result);
    } catch {
      this.showResult({ status: 'uncertain' });
    } finally {
      activeCommit = false;
      this.setBusy(false);
    }
  }

  showResult(result) {
    this.lastResult = result;
    const completed = result.status === 'complete';
    this.review.hidden = !completed && !['partial', 'uncertain', 'price_changed', 'absent'].includes(result.status);
    this.reviewRequired = completed || !this.review.hidden;
    if (completed) {
      this.status.textContent = this.labels.added;
      removeCartOperation(this.storage);
      if (typeof this.selector.clearSelection === 'function') this.selector.clearSelection({ fromCart: true });
      globalThis.location.assign(this.api.cartUrl);
    } else if (result.status === 'price_changed') {
      const total = this.selector.formatMoney(result.actualSelectionTotal, result.currency);
      this.status.textContent = this.labels.priceChanged.replace('{total}', total);
    } else {
      const key = result.status === 'partial' ? 'partial' : result.status === 'currency_changed' ? 'currencyChanged' : ['uncertain', 'absent'].includes(result.status) ? 'uncertain' : 'error';
      this.status.textContent = this.labels[key];
      if (result.mutationAttempted === false || result.status === 'not_added') removeCartOperation(this.storage);
    }
    this.dispatchEvent(new CustomEvent('lups:cart-result', { detail: { status: result.status, priceCheck: result.priceCheck || 'not_comparable', actualSelectionTotal: result.actualSelectionTotal ?? null, cartTotal: result.cartTotal ?? null, currency: result.currency || null }, bubbles: true }));
    this.updateButton();
  }
}

if (globalThis.customElements && !customElements.get('lups-selector-cart')) customElements.define('lups-selector-cart', LupsSelectorCart);
